import {ENTER} from '@angular/cdk/keycodes';
import {AfterViewInit,Component,ElementRef,EventEmitter,Inject,Input,OnDestroy,OnInit,Optional,Output,QueryList,ViewChild,ViewChildren} from '@angular/core';
import {MatLegacyChipInputEvent as MatChipInputEvent} from '@angular/material/legacy-chips';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject,Subscription} from 'rxjs';
import {NgForm} from "@angular/forms";
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {Router} from "@angular/router";
import {AppState} from "@domain/appstate";
import {NiveauAlerte} from '@domain/common/alerte/alerte';
import {Filter,ListView,TypeComparaison,TypeFilter} from '@domain/common/list-view';
import {filterActionVisible} from "@domain/common/list-view/action";
import {ListItem} from '@domain/common/list-view/list-item';
import {CountStatus} from "@domain/common/list-view/list-view";
import {ListViewItem} from '@domain/common/list-view/list-view-item';
import {SearchType} from '@domain/common/list-view/sorting';
import {TypeProfil} from "@domain/user/user";
import {Store} from "@ngrx/store";
import * as settingsActions from "@reducers/settings";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {first} from "rxjs/operators";
import {ListViewService} from './list-view.service';
import {SortingComponent} from './sorting/sorting.component';
import {FloatingButtonService} from "@share/component/floating-button/floating-button.service";
import {ListViewFiltersPopupComponent} from "@share/component/list-view/list-view-filters-popup/list-view-filters-popup.component";
import {MatLegacySelect as MatSelect} from "@angular/material/legacy-select";

@Component({
    host: {'data-test-id': 'list-view-search'},
    selector: 'list-view-search',
    templateUrl: './list-view-search.component.html',
    styleUrls: ['./list-view-search.component.scss']
})
export class ListViewSearchComponent<T extends ListItem,K extends ListViewItem<T>> implements OnInit, OnDestroy, AfterViewInit {
    /* Déclaration pour accès dans le template */
    readonly SearchType = SearchType;
    readonly NiveauAlerte = NiveauAlerte;
    readonly TypeComparaison = TypeComparaison;

    /** Enum des statuts du count pour accès dans le template */
    readonly CountStatus: typeof CountStatus = CountStatus;

    /** Liste à afficher */
    @Input() liste: ListView<T,K>;

    /** Texte recherché */
    searchText: string = '';

    /** Visibilité des filtres */
    isFilterVisible: boolean = false;

    /** Évènement de changement de la visibilité des filtres */
    @Output() filterVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    /** Liste des types de filtre */
    TypeFilter: typeof TypeFilter = TypeFilter;

    /** Liste des séparateurs */
    readonly listeSeparateurs = [ENTER] as const;

    /** Champ de saisie pour la recherche */
    @ViewChild('searchInput',{ static: true }) searchInput: ElementRef;

    /** Numéro de page */
    numPage: number = 0;

    /** Liste des types des comparaisons */
    listeTypesComparaison: Array<{ code:string, libelle:string }>;

    /** Filtre les actions suivant leur visibilité */
    filterActionVisible: Function = filterActionVisible;

    /** Pagination */
    pagination: BehaviorSubject<String> = new BehaviorSubject<String>(null);

    /** Profil de l'utilisateur connecté */
    fonction: TypeProfil;

    /** Liste des souscriptions de la page */
    listeSouscriptions: Array<Subscription> = [];

    /** Référence à la racine du composant */
    @ViewChild('listviewSearch') listviewSearch!: ElementRef;

    /** Évènement de changement de la hauteur du composant */
    @Output() hauteurChange: EventEmitter<number> = new EventEmitter<number>();

    /** Liste des composants de filtre de type select */
    @ViewChildren('filterSelect') filterSelect!: QueryList<MatSelect>;

    /** Liste des composants de filtre de type date */
    @ViewChildren('filterDate') filterDate!: QueryList<ElementRef>;

    /** Liste des composants de filtre de type montant */
    @ViewChildren('filterMontant') filterMontant!: QueryList<ElementRef>;

    /**
     * Constructeur
     *
     * @param listViewService       Service des listes
     * @param translateService      Service de traduction
     * @param store                 Store de l'application
     * @param matDialog             Service des boites de dialogue material
     * @param router                Service de routage
     * @param floatingButtonService Service de gestion des boutons flottants (pour être utilisé, il faut que la directive {@link FloatingButtonDirective} soit bien définie)
     */
    constructor(
        public listViewService: ListViewService<T,K>,
        private translateService: TranslateService,
        private store: Store<AppState>,
        private matDialog: MatDialog,
        private router: Router,
        @Optional() @Inject('FLOATING_BUTTON_SERVICE') private floatingButtonService: FloatingButtonService
    ) { }

    /**
     * Initialisation de la page
     */
    ngOnInit(): void {
        //Liste de types de comparaison
        this.listeTypesComparaison = [{
            code: 'EQUAL',
            libelle: this.translateService.instant('filter.rule.filterOperator.equal')
        },{
            code: 'GREATER',
            libelle: this.translateService.instant('filter.rule.filterOperator.superieure_a')
        },{
            code: 'LESS',
            libelle: this.translateService.instant('filter.rule.filterOperator.inferieure_a')
        },{
            code: 'BETWEEN',
            libelle: this.translateService.instant('filter.rule.filterOperator.compris')
        }];

        //Chargement du paramétrage
        this.store.dispatch({
            type: settingsActions.LOAD_SETTINGS,
            payload: ['Global']
        });

        //Sélection du profil de l'utilisateur
        this.store.select(state => state.session.user).pipe(filterFirstNotNull()).subscribe(user => {
            //Définition du type de profil
            this.fonction = user.fonction;
        });

        //On s'abonne aux demandes de pagination
        this.listeSouscriptions.push(this.liste.updatePagination$.subscribe(() => {
            setTimeout(() => {
                //Rechargement de la pagination au prochain cycle d'angular
                this.doUpdatePagination();
            });
        }));

        setTimeout(() => {
            //Rechargement de la pagination au prochain cycle d'angular
            this.doUpdatePagination();
        });

        //Si la liste est paramétrée pour déplier les filtres par défaut
        if (this.liste.isFilterUnfolded) {
            //Affichage des filtres
            this.isFilterVisible = true;

            //Émission du nouvel état des filtres
            this.filterVisibleChange.emit(this.isFilterVisible);
        }
    }

    /**
     * Vérification du composant
     */
    ngAfterViewInit() {
        //Emission du changement de hauteur après une seconde pour laisser au composant de filtre le temps de se charger
        setTimeout(() => this.hauteurChangeEvent(), 1000);
    }

    /**
     * Mise à jour de la pagination
     */
    private doUpdatePagination() {
        if (this.liste.showPagination) {
            this.pagination.next(this.listViewService.getPagination(this.liste, this.fonction));
        }
    }

    /**
     * Mise à jour de la pagination selon les données contenues dans la liste
     */
    refreshListPagination() {
        if (this.liste.showPagination) {
            setTimeout(() => {
                //Rechargement de la pagination au prochain cycle d'angular avec les nouvelles données dans la liste
                this.doUpdatePagination();
            });
        }
    }

    /**
     * Ouverture du composant de filtre correspondant
     *
     * @param filter filtre à ouvrir
     */
    openMatchingFilter(filter: Filter): void {
        //Déploiement du panneau des filtres
        this.filterVisible(true);

        //Si le filtre est de type select
        if (filter.listeValues) {
            //Ouverture du select associé (hors cycle afin de laisser le temps au panneau de se déployer et le ViewChildren de récupérer les infos)
            setTimeout(() => this.filterSelect?.find(component => component.id == filter.clef)?.open());
        } else if (filter.type == TypeFilter[TypeFilter.DATE]) {
            //Sinon, si le filtre est de type date, alors ouverture du datepicker associé (hors cycle afin de laisser le temps au panneau de se déployer et le ViewChildren de récupérer les infos)
            setTimeout(() => this.filterDate?.find(component => component.nativeElement?.id == `${filter.clef}_dateDebut`)?.nativeElement?.focus());
        } else if (filter.type == TypeFilter[TypeFilter.DECIMAL]) {
            //Sinon, si le filtre est de type montant, alors ouverture du montant associé (hors cycle afin de laisser le temps au panneau de se déployer et le ViewChildren de récupérer les infos)
            setTimeout(() => this.filterMontant?.find(component => component.nativeElement?.id == `${filter.clef}_min`)?.nativeElement?.focus());
        }
    }

    /**
     * Récupération du filtre pour les bulles
     */
    isChipVisible() {
        //Vérification du filtre et de la visibilité de l'élément
        return (item: Filter) => !item.hiddenChip;
    }

    /**
     * Changement de valeur sur un select
     * @param filter le filtre
     */
    filterSelectionChange(filter: Filter): void {
        //Si le filtre comporte une methode de MAJ
        if (filter.changeMethod) {
            //Emmission de la MAJ
            filter.changeMethod(filter);
        }
    }

    /**
     * Rafraîchissement de la liste
     * @param numPage numéro de page
     */
    refreshData(numPage: number = 0): void {
        //Si la liste est alimentée par le front, qu'elle n'est pas "simple" ou "locale", que les filtres sont activés et qu'il n'y a pas de pagination locale (avec les flèches)
        if (this.liste.isFrontendList && !(this.liste.isSimple || this.liste.isLocal) && (this.liste.isFilter || this.liste.sorting != null)) {
            if (this.liste.onRefresh != null) {
                this.liste.onRefresh(this.liste,null);
            } else {
                this.listViewService.searchAndSortFrontendList(this.liste);
            }

            this.liste.data.numPage = 0;
            this.liste.data.nbPagesTotal = 1;
            this.liste.data.nbObjetsTotal = this.liste.data.listeResultats.length;

            //Mise à jour du nombre d'éléments visibles dans la page
            this.liste.data.nbObjetsDansPage = this.liste.data.listeResultats.filter(data => !data.isFiltre).length;
            this.liste.data.nbObjetsParPage = this.liste.data.nbObjetsDansPage;
        } else {
            //Initialisation
            let subscription: Subscription;

            //Vérification du numéro de page demandé
            if (numPage == 0) {
                //Remise à zéro du numéro de page courant
                this.numPage = 0;

                //On s'assure de supprimer le count précédent
                this.liste.countTotal = -1;

                //S'il y a une gestion du count
                if (this.liste.loadCount) {
                    //On le reset
                    this.liste.loadCount.loadStatus.next(CountStatus.NONE);
                }

                //On met à jour la pagination
                this.doUpdatePagination();
            }

            //Vidage de la liste
            this.liste.data = null;

            //Indicateur de chargement actif
            this.liste.isLoading = true;

            //Purge des données préchargées
            this.liste.isPreloading = false;
            if (this.liste.preloadSubscription) {
                this.liste.preloadSubscription.unsubscribe();
                this.liste.preloadSubscription = null;
            }
            this.liste.preloadedData = new BehaviorSubject<any>(undefined);

            //Chargement de la liste
            subscription = this.listViewService.loadData(this.liste,this.liste.uri,numPage,this.liste.nbObjetsParPage,this.liste.defaultOrder,this.liste.listeStaticFilters,this.liste.listeSelectedFilters, this.fonction).subscribe((data) => {
                //Mise à jour de la liste
                this.liste.data = data;

                //Rafraichissement des données
                this.liste.onRefresh && this.liste.onRefresh(this.liste, data);

                //Sauvegarde des paramètres appliqués
                this.listViewService.backupListeParams(this.liste, this.fonction);

                //Réinitialisation de previousLength et hasNewData pour permettre le chargement de toutes les pages
                this.liste.previousLength = -1;
                this.liste.hasNewData = true;

                //Pagination
                this.doUpdatePagination();

                //Suppression de la souscription
                subscription && subscription.unsubscribe();

                //Indicateur de chargement inactif
                this.liste.isLoading = false;

                //Préchargement des données suivantes
                this.listViewService.preloadNextData(this.liste, this.liste.uri, numPage, this.liste.nbObjetsParPage, this.liste.defaultOrder, this.liste.listeStaticFilters, this.liste.listeSelectedFilters, this.fonction);
            });
        }
    }

    /**
     * Ajout d'un filtre
     * @param event événement
     */
    addFilter(event: MatChipInputEvent): void {
        //Regex permettant d'identifiant une valeur numérique d'un filtre de recherche
        const NUMBER_REGEX = /^[0-9]+([.,][0-9]+)?$/;

        //Initialisation
        let value: string;

        //Lecture de la valeur saisie
        value = (event.value || '').trim();

        //Vérification de la valeur
        if (value) {
            //Vérification d'une valeur numérique
            const isNumericValue = NUMBER_REGEX.test(value);

            //Ajout du filtre
            this.liste.listeSelectedFilters.push({
                clef: this.liste.listeFilters
                    //On ne prend que les filtres par défaut, et si la valeur recherchée n'est pas un nombre alors on ignore tous les filtres qui sont de type LONG ou DECIMAL
                    .filter(f => f.isDefault && (!isNumericValue && (f.type == TypeFilter[TypeFilter.LONG] || f.type == TypeFilter[TypeFilter.DECIMAL]) ? false : true))
                    .map(f => f.clefSearchbar ?? f.clef).join(','),
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
				valeur: `${value}` + (this.liste.isFrontendList ? '' : '%'),
                displayedValeur: value,
				isSearchbarFilter: true,
				timestamp: new Date().getTime()
            });

            //Rafraichissement de la liste
            this.refreshData();
        }

        //Suppression de la saisie
        if (event.chipInput) {
            event.chipInput.clear();
        }
    }

    /**
     * Focus sur la barre de recherche
     */
    onSearchFocus() {
        if (!this.liste.isSimple && !this.liste.isDashboardList) {
            //Focus sur le champ de saisie
            this.searchInput?.nativeElement.focus();
        }
    }

    /**
     * Mise à jour des filtres quand on presse le bouton entrer dans un filtre
     *
     * @param form le formulaire pour contrôler qu'il est valide
     */
    onPressEnterInFilter(form?: NgForm) {
        //On met à jour les filtres uniquement si la liste n'est pas en cours de chargement et si le formulaire est valide
        if (!this.liste.isLoading && (!form || form?.valid)) {
            this.updateListeFilters();
        }
    }

    /**
     * Mise à jour de la liste des filtres et Rafraichissement des données de la liste
     */
    async updateListeFilters() {
        //Rafraichissement des filtres
        this.listViewService.refreshListeSelectedFilters(this.liste);

        //Fonction spécifique au moment de la recherche
        this.liste.onSearch && await this.liste.onSearch(this.liste);

        //Rafraichissement de la liste
        this.refreshData();

        //Replie du panneau
        this.isFilterVisible = false;

        //Détection du changement de hauteur
        this.hauteurChangeEvent();
    }

    /**
     * Réinitialisation des filtres
     */
    resetListeFilters() {
        //Récupération des filtres actifs ayant une implémentation custom de la methode de suppression
        const listeSelectedFilters = this.liste.listeSelectedFilters?.filter(filter => !!filter.removeMethod);

        //Désélection des filtres visibles et réinitialisation de la valeur
        this.liste.listeFilters = this.liste.listeFilters.map(filter => ({
            ...filter,
            isSelected: false,
            valeur: null,
            min: null,
            max: null,
            dateDebut: null,
            dateFin: null
        }));

        //Suppression des filtres sélectionnés
        this.liste.listeSelectedFilters = [];

        listeSelectedFilters.forEach(filter => {
            filter.removeMethod(this.liste.listeFilters.find(f => f.clef == filter.clef));
        });

        //Méthode optionnelle spécifique pour le reset des filtres
        this.liste.resetFilters && this.liste.resetFilters();

        //Masquage du panneau de filtres
        this.isFilterVisible = false;

        //Détection du changement de hauteur
        this.hauteurChangeEvent();

        //Rafraichissement de la liste
        this.refreshData();
    }

    /**
     * Page précédente
     */
    goToPreviousPage(): void {
        //Vérification de la possibilité
        if (this.numPage > 0) {
            //Rafraichissement de la liste
            this.refreshData(--this.numPage);
        }
    }

    /**
     * Page suivante
     */
    goToNextPage(): void {
        //Vérification de la possibilité
        if (this.numPage < (this.liste.data.nbPagesTotal - 1)) {
            //Rafraichissement de la liste
            this.refreshData(++this.numPage);
        }
    }

    /**
     * Affichage de la popup de tri
     */
    showSortPopup(): void {
        //Affichage de la popup
        this.matDialog.open(SortingComponent,{
            data: {
                sorting: this.liste.sorting
            }
        }).afterClosed().subscribe({
            next: retour => {
                //Vérification
                if (retour) {
                    //Cas du reset
                    if (retour.reset) {
                        //Mise à jour du sorting
                        this.liste.defaultOrder = this.liste.sorting.reset();
                        //Rafraîchissement de la liste
                        this.updateListeFilters();
                    }
                    //Cas du nouveau tri
                    else if (retour.sorting) {
                        //Mise à jour du sorting
                        this.liste.defaultOrder = this.liste.sorting.getFormattedSorting();
                        //Rafraîchissement de la liste
                        this.updateListeFilters();
                    }
                }
            }
        });
    }

    /**
     * Redirige vers la liste correspondante (uniquement depuis une dashboard list)
     */
    goToList(): void {
        this.router.navigate([this.liste.redirect]);
    }

    /**
     * Demande de chargement du count
     */
    loadCount() {
        //Chargement du count
        this.listViewService.loadCount(this.liste).pipe(first()).subscribe(_ => {
            //Mise à jour de la pagination
            this.doUpdatePagination();
        })
    }

    /**
     * Reset les boutons flottants s'il y en a pour la liste
     */
    resetButtons(): void {
        //Si on a un service de gestion des boutons flottants
        if (!!this.floatingButtonService) {
            //On reset les boutons
            this.floatingButtonService.resetFloatingButtonActions();

            //On désélectionne la liste
            this.liste.selectAll(false);
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy(): void {
        //Unsub de toutes les souscriptions
        this.listeSouscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Indication de l'affichage de la barre de recherche
     * @return true si on doit afficher la search bar
     */
    hasSearchBarVisible(): boolean {
        //Si on a spécifié qu'on devait afficher la search bar
        if (this.liste.isSearchBar != null) {
            return this.liste.isSearchBar;
        }
        //On renvoie les valeurs historiques qui modifient également d'autres éléments que la searchbar
        return !this.liste.isSimple && !this.liste.isDashboardList && !this.liste.isFrontendList
    }

    /**
     * Indique si on doit afficher le bouton pour le paramétrage des filtres
     * @return true si on doit afficher le bouton pour le paramétrage des filtres
     */
    hasFilterVisible(): boolean {
        //Si on a spécifié qu'on devait afficher le bouton de filtre
        if (this.liste.isFilterVisible != null) {
            return this.liste.isFilterVisible
        }

        //on renvoie les valeurs historiques
        return (!this.liste.isLocal || this.liste.isFilter) && !this.liste.isFrontendList && !this.liste.isSimple && !this.liste.isDashboardList
    }

    /**
     * Affichage ou masquage des filtres
     *
     * @param isVisibleForced true si affichage forcé
     */
    filterVisible(isVisibleForced: boolean = false): void {
        //Affichage ou masquage des filtres
        this.isFilterVisible = isVisibleForced ? true : !this.isFilterVisible;

        //Émission du nouvel état des filtres
        this.filterVisibleChange.emit(this.isFilterVisible);

        //Focus sur la recherche
        this.onSearchFocus();

        //Détection du changement de hauteur
        this.hauteurChangeEvent();
    }

    /**
     * Emission du changement de hauteur
     */
    hauteurChangeEvent(): void {
        //Emission du changement de hauteur (sortie du cycle standard pour que le composant ait le temps de prendre sa nouvelle dimension)
        setTimeout(() => this.hauteurChange.emit(this.listviewSearch.nativeElement.offsetHeight));
    }

    /**
     * Affichage de la fenêtre de sélection des filtres
     */
    openFiltersPopup($event: Event) {
        //Interruption des évènements suivants
        $event.preventDefault();
        $event.stopPropagation();

        //Ouverture de la fenêtre des filtres
        !(this.liste?.isLoading) && this.matDialog.open(ListViewFiltersPopupComponent, {
            width: '80%',
            data: {
                listeFilters: this.liste.listeFilters,
                filterClick: () => this.hauteurChangeEvent()
            }
        });

        //Ouverture forcée du tiroir des filtres
        !(this.liste?.isLoading) && this.filterVisible(true);
    }


    /**
     * Inversion du mode de recherche pour la barre de recherche et déclenchement de la recherche si au moins un filtre est présent
     */
    toggleSearchType(): void {
        //List-view associée à la recherche
        const liste: ListView<T,K> = this.liste;

        //Inversion du mode de recherche
        if (!liste.searchType || liste.searchType == SearchType.STARTS_WITH) {
            liste.searchType = SearchType.CONTAINS;
        } else {
            liste.searchType = SearchType.STARTS_WITH;
        }

        //Synchronisation avec le tri si présent
        if (liste.sorting) {
            liste.sorting.search = liste.searchType;
        }

        //Vérification de la présence de filtres
        if (liste.listeSelectedFilters?.length > 0) {
            //Rechargement
            this.refreshData();
        }
    }

    /**
     * Inversion du mode de recherche pour le panneau des filtres (sorting) et synchronisation
     */
    toggleSortingSearch(): void {
        //List-view associée à la recherche
        const liste: ListView<T,K> = this.liste;

        if (liste.sorting) {
            //Inversion du mode de recherche
            liste.sorting.toggleSearch();
            //Synchronisation avec la barre de recherche globale
            liste.searchType = liste.sorting.search;

            //Si des filtres sont présents, on recharge les données de la liste
            if (liste.listeSelectedFilters?.length > 0) {
                this.refreshData();
            }
        }
    }
}
